import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Home,
  Folder,
  Dashboard,
  DataUsage,
  Edit,
  ModelTraining,
  SwapHoriz,
  Assessment,
  Settings,
  Info,
  HelpOutline,
} from '@mui/icons-material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

const drawerWidth = 260;

const Sidebar = ({ open, onClose, isMobile }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();

  const mainMenuItems = [
    { text: 'Home', icon: <Home />, path: '/' },
    { text: 'Projects', icon: <Folder />, path: '/projects' },
    { text: 'About', icon: <Info />, path: '/about' },
    { text: 'Help', icon: <HelpOutline />, path: '/help' },
  ];

  const projectMenuItems = projectId
    ? [
        { text: 'Dashboard', icon: <Dashboard />, path: `/project/${projectId}/dashboard` },
        { text: 'Dataset', icon: <DataUsage />, path: `/project/${projectId}/dataset` },
        { text: 'Editor', icon: <Edit />, path: `/project/${projectId}/editor` },
        { text: 'AI Models', icon: <ModelTraining />, path: `/project/${projectId}/ai` },
        { text: 'Cal. Transfer', icon: <SwapHoriz />, path: `/project/${projectId}/transfer` },
        { text: 'Reports', icon: <Assessment />, path: `/project/${projectId}/reports` },
        { text: 'Settings', icon: <Settings />, path: `/project/${projectId}/settings` },
      ]
    : [];

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const isActive = (path) => location.pathname === path;

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: 'white',
      }}
    >
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: alpha('#ffffff', 0.2),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DataUsage sx={{ fontSize: 28 }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Digital Spectra
        </Typography>
      </Box>

      <Divider sx={{ borderColor: alpha('#ffffff', 0.1) }} />

      <List sx={{ px: 2, py: 2 }}>
        {mainMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              onClick={() => handleNavigate(item.path)}
              sx={{
                borderRadius: 2,
                color: 'white',
                backgroundColor: isActive(item.path)
                  ? alpha('#ffffff', 0.2)
                  : 'transparent',
                '&:hover': {
                  backgroundColor: alpha('#ffffff', 0.15),
                },
                transition: 'all 0.2s',
              }}
            >
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontWeight: isActive(item.path) ? 600 : 500,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {projectMenuItems.length > 0 && (
        <>
          <Divider sx={{ borderColor: alpha('#ffffff', 0.1), mx: 2 }} />
          <Box sx={{ px: 3, py: 2 }}>
            <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 600 }}>
              PROJECT WORKSPACE
            </Typography>
          </Box>
          <List sx={{ px: 2 }}>
            {projectMenuItems.map((item) => (
              <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => handleNavigate(item.path)}
                  sx={{
                    borderRadius: 2,
                    color: 'white',
                    backgroundColor: isActive(item.path)
                      ? alpha('#ffffff', 0.2)
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: alpha('#ffffff', 0.15),
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: isActive(item.path) ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={open}
          onClose={onClose}
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              border: 'none',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              border: 'none',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};

export default Sidebar;
